import { Grid, Popover, Space } from '@arco-design/web-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Form } from 'react-final-form';
import { IconFont, Stack, TextStyle } from '@bay-email/editor';
import { SearchField, SwitchField } from '@extensions/components/Form';
import { ToolItem } from '../ToolItem';
import { useTiptapEditor } from '../Tools/TiptapEditorContext';
import { EMAIL_BLOCK_CLASS_NAME, normalizeLinkHref, t } from '@bay-email/core';

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface LinkParams {
  link: string;
  blank: boolean;
  underline: boolean;
  linkNode: HTMLAnchorElement | null;
}

export interface LinkProps {
  currentRange: Range | null | undefined;
  onChange: (val: LinkParams) => void;
  getPopupContainer: () => HTMLElement;
}

function getAnchorElement(
  node: Node | null,
): HTMLAnchorElement | null {
  if (!node) return null;
  if (node instanceof HTMLAnchorElement) {
    return node;
  }
  if (node instanceof Element && node.classList.contains(EMAIL_BLOCK_CLASS_NAME)) return null;

  return getAnchorElement(node.parentNode);
}

export function getLinkNode(
  currentRange: Range | null | undefined,
): HTMLAnchorElement | null {
  let linkNode: HTMLAnchorElement | null = null;
  if (!currentRange) return null;
  linkNode = getAnchorElement(currentRange.startContainer);
  return linkNode;
}

export function Link(props: LinkProps) {
  const tiptapEditor = useTiptapEditor();
  const [visible, setVisible] = useState(false);
  const [highlightRects, setHighlightRects] = useState<HighlightRect[]>([]);
  const [formResetVersion, setFormResetVersion] = useState(0);
  const preservedRangeRef = useRef<Range | null>(null);
  const didApplyRef = useRef(false);

  const clearHighlight = useCallback(() => {
    // Remove all temporary overlay rectangles.
    setHighlightRects([]);
  }, []);

  const syncPreservedRange = useCallback(() => {
    // Keep a clone of the user's last non-collapsed selection for highlighting.
    if (!props.currentRange || props.currentRange.collapsed) {
      preservedRangeRef.current = null;
      return;
    }
    preservedRangeRef.current = props.currentRange.cloneRange();
  }, [props.currentRange]);

  const showHighlight = useCallback(() => {
    const range = preservedRangeRef.current;
    if (!range || range.collapsed) {
      clearHighlight();
      return;
    }

    // A range can span multiple visual lines, so we render one overlay per DOMRect.
    // Guard against stale/detached ranges throwing while reading client rects.
    try {
      const nextRects = Array.from(range.getClientRects())
        .filter(rect => rect.width > 0 && rect.height > 0)
        .map(rect => ({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        }));

      setHighlightRects(nextRects);
    } catch {
      clearHighlight();
    }
  }, [clearHighlight]);

  useEffect(() => {
    syncPreservedRange();
  }, [syncPreservedRange]);

  useEffect(() => {
    if (!visible || highlightRects.length === 0) return;

    const updateHighlight = () => {
      showHighlight();
    };

    // The editor can scroll inside ShadowRoot containers; listen on all relevant
    // scroll targets so the temporary highlight stays aligned while scrolling.
    const rangeRoot = preservedRangeRef.current?.startContainer?.getRootNode?.();
    const hasCustomRoot = Boolean(
      rangeRoot &&
      rangeRoot !== document &&
      'addEventListener' in (rangeRoot as EventTarget),
    );

    window.addEventListener('resize', updateHighlight);
    window.addEventListener('scroll', updateHighlight, true);
    document.addEventListener('scroll', updateHighlight, true);
    if (hasCustomRoot) {
      (rangeRoot as EventTarget).addEventListener('scroll', updateHighlight, true);
    }

    return () => {
      window.removeEventListener('resize', updateHighlight);
      window.removeEventListener('scroll', updateHighlight, true);
      document.removeEventListener('scroll', updateHighlight, true);
      if (hasCustomRoot) {
        (rangeRoot as EventTarget).removeEventListener('scroll', updateHighlight, true);
      }
    };
  }, [highlightRects.length, showHighlight, visible]);

  const initialValues = useMemo((): LinkParams => {
    let link = '';
    let blank = true;
    let underline = true;
    let linkNode: HTMLAnchorElement | null = getLinkNode(props.currentRange);
    if (tiptapEditor) {
      // Read the mark, not the DOM: the toolbar writes through the editor.
      const attrs = tiptapEditor.getAttributes('link');
      link = attrs.href || '';
      blank = link ? attrs.target === '_blank' : true;
      underline = link ? /text-decoration:\s*underline/.test(attrs.style || '') : true;
      linkNode = null;
    } else if (linkNode) {
      link = linkNode.getAttribute('href') || '';
      blank = linkNode.getAttribute('target') === '_blank';
      underline = linkNode.style.textDecoration === 'underline';
    }
    return {
      link,
      blank,
      underline,
      linkNode,
    };
  }, [props.currentRange, tiptapEditor]);

  const onSubmit = useCallback(
    (values: LinkParams) => {
      // Mark as applied so close logic does not reset form state again.
      didApplyRef.current = true;
      clearHighlight();
      // Text links are written into the HTML content, so they are made absolute
      // here rather than at render time like block hrefs (monday 13291045384).
      props.onChange({ ...values, link: normalizeLinkHref(values.link) });
      setVisible(false);
    },
    [clearHighlight, props]
  );

  const handleVisibleChange = useCallback((nextVisible: boolean) => {
    // Capture selection on open; cleanup highlight and unsaved form edits on close.
    if (nextVisible) {
      didApplyRef.current = false;
      syncPreservedRange();
      setVisible(true);
      return;
    }

    clearHighlight();
    // Closing without Apply should discard unsaved input edits.
    if (!didApplyRef.current) {
      setFormResetVersion(prev => prev + 1);
    }
    setVisible(false);
  }, [clearHighlight, syncPreservedRange]);

  const handleSearchFieldFocus = useCallback(() => {
    // Show temporary selection highlight while focus is in the link input.
    syncPreservedRange();
    showHighlight();
  }, [showHighlight, syncPreservedRange]);

  return (
    <>
      <Form
        key={`${initialValues.link}-${formResetVersion}`}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({ handleSubmit }) => {
          return (
            <Popover
              trigger='click'
              color='#fff'
              position='bl'
              popupVisible={visible}
              onVisibleChange={handleVisibleChange}
              getPopupContainer={props.getPopupContainer}
              content={(
                <div style={{
                  color: '#333',
                  width: 300,
                }}>
                  <Stack vertical spacing='none'>
                    <SearchField
                      size='small'
                      name='link'
                      label={t('Link')}
                      labelHidden
                      searchButton={t('Apply')}
                      placeholder={t('https://www.example.com')}
                      onFocus={handleSearchFieldFocus}
                      onClick={handleSearchFieldFocus}
                      onSearch={() => handleSubmit?.()}
                    />
                  </Stack>
                  <Grid.Row>
                    <Grid.Col span={12}>
                      <Space align='center' size='mini'>
                        <TextStyle size='smallest'>{t('Target')}</TextStyle>
                        <SwitchField
                          size='small'
                          label={t('Target')}
                          labelHidden
                          name='blank'
                          checkedText={t('blank')}
                          uncheckedText={t('self')}
                          formItem={{ className: 'm-0' }}
                          inline
                        />
                      </Space>
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Space align='center' size='mini'>
                        <TextStyle size='smallest'>{t('Underline')}</TextStyle>
                        <SwitchField
                          size='small'
                          label={t('Underline')}
                          labelHidden
                          name='underline'
                          checkedText={t('off')}
                          uncheckedText={t('on')}
                          formItem={{ className: 'm-0' }}
                          inline
                        />
                      </Space>
                    </Grid.Col>
                  </Grid.Row>
                </div>
              )}
            >
              <ToolItem
                isActive={Boolean(initialValues.link)}
                title={t('Link')}
                icon={<IconFont iconName='icon-link' />}
              />
            </Popover>
          );
        }}
      </Form>
      {typeof document !== 'undefined' && highlightRects.length > 0 && ReactDOM.createPortal(
        <>
          {highlightRects.map((rect, index) => (
            <div
              key={`${rect.left}-${rect.top}-${index}`}
              style={{
                position: 'fixed',
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                backgroundColor: 'rgba(14, 101, 235, 0.22)',
                borderRadius: 2,
                pointerEvents: 'none',
              }}
            />
          ))}
        </>,
        document.body,
      )}
    </>
  );
}
